
import { action, observable, makeObservable } from "mobx";
import { api } from "../lib/api";
import { SharedStateStore } from "./SharedStateStore";
import { IDistrict, ISchool, IPost } from "../models/apiDataStructures";
import { IOrganizationContentResponse } from "../models/apiResponses";
import { IOrganizationContentApiBody } from "../models/apiRequests";

const DEFAULT_RECORDS_PER_PAGE = 5

export type IOrganizationContentType = "district-content" | "school-content"

interface IOrganizationLoadPostOptions {
    contentType: IOrganizationContentType
    keyword?: string
    page?: number
    districtOverride?: IDistrict
    schoolOverride?: ISchool
}

export class OrganizationContentStore extends SharedStateStore {

    posts: IPost[] = [];
    loading = true;
    firstRender = true;
    totalPages = 1;
    page = 1;
    keyword = "";

    constructor() {
        super()

        makeObservable(this, {
            posts: observable,
            firstRender: observable,
            loading: observable,
            totalPages: observable,
            page: observable,
            loadPosts: action,
            editPost: action,
            deletePost: action
        })
    }

    async loadPosts(options: IOrganizationLoadPostOptions){

        let { contentType, keyword, page, districtOverride, schoolOverride } = options

        if (!keyword) keyword = ""
        if (!page) page = 1

        this.setState({ loading: true });
        if (keyword) this.keyword = keyword;
        let override = ""
        if (contentType === 'district-content' && districtOverride) override = `&id=${districtOverride.id}`
        if (contentType === 'school-content' && schoolOverride) override = `&id=${schoolOverride.id}`

        try {
            let url = keyword
                ? `${contentType}?search=${encodeURIComponent(keyword)}&page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`
                : `${contentType}?page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`;
            if (override) url = url + override
            const response = await api.find<IOrganizationContentResponse>(url);

            let posts = response.results

            this.setState({
                posts: posts,
                page: response.current,
                totalPages: response.totalPages
            })
        } catch (err) {
            throw err;
        } finally {
            
            this.setState({ loading: false, firstRender: false })
        }
    }

    async editPost(
        contentType: IOrganizationContentType,
        postId: number,
        newTitle: string,
        newContent: string,
        isPinned: boolean,
        isEducatorOnly: boolean
    ) {

        const postIndex = this.posts.findIndex(post => post.id === postId);
        const oldPost = this.posts[postIndex]
        const request = {
            ...oldPost,
            content: newContent,
            title: newTitle,
            pinned: isPinned,
            educatorOnly: isEducatorOnly
        };

        try {
            await api.put<IPost, IOrganizationContentApiBody>(
                `${contentType}/${postId}`,
                request
            );
        } catch (err) {
            throw err
        } finally {
            this.loadPosts({
                contentType: contentType,
                keyword: this.keyword,
                page: this.page,
                districtOverride: oldPost.district || undefined,
                schoolOverride: oldPost.school || undefined
            })
        }

    }

    async deletePost(contentType: IOrganizationContentType, postId: number) {
        await api.delete(`${contentType}/${postId}`);
        let posts = this.posts.filter((post, index) => post.id !== postId);
        this.setState({ posts })
    }

    async createDistrictPost(title: string, content: string, pinned: boolean, educatorOnly: boolean, district?: IDistrict) {

        if (!district) {
            throw new Error("Unknown district");
        }

        try {
            await api.create<IPost, IOrganizationContentApiBody>(
                "district-content/",
                {
                    district: {
                        id: district.id,
                        name: district.name
                    },
                    title,
                    content,
                    pinned,
                    educatorOnly
                }
            )
        } catch (err) {
            throw err;
        } finally {
            this.loadPosts({
                contentType: 'district-content',
                keyword: this.keyword,
                page: this.page,
                districtOverride: district
            })
        }
    }


    async createSchoolPost(title: string, content: string, pinned: boolean, educatorOnly: boolean, school?: ISchool) {

        if (!school) {
            throw new Error("Unknown school");
        }

        try {
            await api.create<IPost, IOrganizationContentApiBody>(
                "school-content/",
                {
                    school: {
                        id: school.id,
                        name: school.name
                    },
                    title,
                    content,
                    pinned,
                    educatorOnly
                }
            )
        } catch (err) {
            throw err;
        } finally {
            this.loadPosts({
                contentType: 'school-content',
                keyword: this.keyword,
                page: this.page,
                schoolOverride: school
            })
        }
    }


}